import { ReactElement } from 'react';

import { useField } from 'react-final-form';

import { PreparedWorkload } from 'features/workload-optimization';

import { ContainerSettingsChips } from './ContainerSettingsChips';
import { CustomMetricsSettingsArrayField } from './CustomMetricsSettingsArrayField';
import { WorkloadConstraintsSettingsArrayField } from './WorkloadConstraintsSettingsArrayField';
import { getAntiAffinity } from '../../../../../_utils';
import { ApplyTypeField } from '../../../../../_woop-settings/_components/fields/ApplyTypeField';
import { ContainerExclusionField } from '../../../../../_woop-settings/_components/fields/ContainerExclusionField';
import { HpaConverterField } from '../../../../../_woop-settings/_components/fields/HpaConverterField';
import { LookbackField } from '../../../../../_woop-settings/_components/fields/LookbackField';
import { MemoryGrowthDetectionField } from '../../../../../_woop-settings/_components/fields/MemoryGrowthDetectionField';
import { OptimizationThresholdField } from '../../../../../_woop-settings/_components/fields/OptimizationThresholdField';
import { OptimizedResourcesField } from '../../../../../_woop-settings/_components/fields/OptimizedResourcesField';
import { OverheadField } from '../../../../../_woop-settings/_components/fields/OverheadField';
import { PredictiveScalingField } from '../../../../../_woop-settings/_components/fields/PredictiveScalingField';
import { RecommendationPercentileField } from '../../../../../_woop-settings/_components/fields/RecommendationPercentileField';
import { ResourceLimitsField } from '../../../../../_woop-settings/_components/fields/ResourceLimitsField';
import { RolloutBehaviourField } from '../../../../../_woop-settings/_components/fields/RolloutBehaviourField';
import { StallDetectionField } from '../../../../../_woop-settings/_components/fields/StallDetectionField';
import { StartupMetricsField } from '../../../../../_woop-settings/_components/fields/StartupMetricsField';
import { WhenToApplyField } from '../../../../../_woop-settings/_components/fields/WhenToApplyField';
import { AutoscalerSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/AutoscalerSettingsContainer';
import { FormSection } from '../../../../../_woop-settings/_components/settings-containers/components/FormSection';
import { ExcludedContainersSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/ExcludedContainersSettingsContainer';
import { HpaConverterSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/HpaConverterSettingsContainer';
import { LookbackPeriodSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/LookbackPeriodSettingsContainer';
import { MemoryGrowthDetectionSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/MemoryGrowthDetectionSettingsContainer';
import { OptimizedResourcesSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/OptimizedResourcesSettingsContainer';
import { OverheadSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/OverheadSettingsContainer';
import { PredictiveScalingSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/PredictiveScalingSettingsContainer';
import { RecommendationPercentileSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/RecommendationPercentileSettingsContainer';
import { ResourceLimitsSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/ResourceLimitsSettingsContainer';
import { StallDetectionSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/StallDetectionSettingsContainer';
import { StartupMetricsSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/StartupMetricsSettingsContainer';
import { WorkloadConstraintsSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/WorkloadConstraintsSettingsContainer';
import { WorkloadCustomMetricsSettingsContainer } from '../../../../../_woop-settings/_components/settings-containers/WorkloadCustomMetricsSettingsContainer';
import { vpaSettingsConstants } from '../../../../../_woop-settings/_constants/settingsConstants';
import { useVpaSelectedSettingName } from '../../../../../_woop-settings/vpaSelectedSettingNameContexts';
import {
  Path,
  VerticalRightsizingPolicyFormModel,
  WorkloadDetailsFormModel,
} from '../../../_types';
import { OverrideGroupOverrideButton } from '../../override/OverrideGroupOverrideButton';
import { OverrideGroupPencilLabel } from '../../override/OverrideGroupPencilLabel';
import { OverrideGroupProvider } from '../../override/OverrideGroupProvider';
import { WorkloadVpaFormSettings } from '../_constants';

import { RffControlsProvider } from '@cast/shared-rff-ui';

const AutoscalerSettings = () => {
  const path: Path<WorkloadDetailsFormModel> =
    'verticalRightsizing.policy.applyType.value';

  const applyType =
    useField<VerticalRightsizingPolicyFormModel['applyType']['value']>(path)
      .input.value;

  const { antiAffinity } = vpaSettingsConstants.autoscaler;

  return (
    <AutoscalerSettingsContainer
      isAutoscalerModeImmediately={applyType === 'IMMEDIATE'}
      autoscalerModeField={
        <OverrideGroupProvider name="applyType">
          <WhenToApplyField name="value" showTooltip />
        </OverrideGroupProvider>
      }
      rolloutBehaviorField={
        <OverrideGroupProvider name="rolloutBehavior">
          <RolloutBehaviourField LabelComponent={OverrideGroupPencilLabel} />
        </OverrideGroupProvider>
      }
      optimizationThresholdField={
        <OverrideGroupProvider name="optimizationThreshold">
          <OptimizationThresholdField
            LabelComponent={OverrideGroupPencilLabel}
            size="large"
            layout="horizontal"
          />
        </OverrideGroupProvider>
      }
      downscalingModeField={
        <OverrideGroupProvider name="downscaling">
          <ApplyTypeField name="value" testId="downscaling-field" />
        </OverrideGroupProvider>
      }
      antiAffinityField={
        <ApplyTypeField name="value" testId="anti-affinity-field" />
      }
      renderAntiAffinityFormSection={(children) => (
        <OverrideGroupProvider name="antiAffinity" canOverride>
          <FormSection
            title={antiAffinity.title}
            description={antiAffinity.description}
            action={
              <OverrideGroupOverrideButton<
                VerticalRightsizingPolicyFormModel['antiAffinity']
              >
                onRevert={(workload) => ({
                  value: getAntiAffinity({
                    antiAffinity:
                      workload.verticalScalingPolicy?.recommendationPolicies
                        .antiAffinity,
                  }),
                })}
              />
            }
          >
            {children}
          </FormSection>
        </OverrideGroupProvider>
      )}
      memoryEventField={
        <OverrideGroupProvider name="memoryEvent">
          <ApplyTypeField name="value" testId="memory-event-field" />
        </OverrideGroupProvider>
      }
    />
  );
};

export const VpaSettingsContent = ({
  workload,
}: {
  workload: PreparedWorkload;
}) => {
  const { settingName } = useVpaSelectedSettingName<WorkloadVpaFormSettings>();

  const settingContent: Record<WorkloadVpaFormSettings, ReactElement> = {
    customMetrics: (
      <WorkloadCustomMetricsSettingsContainer
        adornment={
          <ContainerSettingsChips
            workload={workload}
            fieldName="customMetrics"
          />
        }
        customMetricsField={
          <CustomMetricsSettingsArrayField workload={workload} />
        }
      />
    ),
    optimizedResources: (
      <OptimizedResourcesSettingsContainer
        optimizedResourcesField={
          <OverrideGroupProvider name="optimizedResources">
            <OptimizedResourcesField />
          </OverrideGroupProvider>
        }
      />
    ),
    excludedContainers: (
      <ExcludedContainersSettingsContainer
        containerExclusionField={
          <OverrideGroupProvider name="excludedContainers">
            <ContainerExclusionField />
          </OverrideGroupProvider>
        }
      />
    ),
    autoscaler: <AutoscalerSettings />,
    overhead: (
      <OverheadSettingsContainer
        overheadField={
          <OverrideGroupProvider name="overhead">
            <OverheadField LabelComponent={OverrideGroupPencilLabel} />
          </OverrideGroupProvider>
        }
      />
    ),
    memoryGrowthDetection: (
      <MemoryGrowthDetectionSettingsContainer
        memoryGrowthDetectionField={
          <OverrideGroupProvider name="memoryGrowthDetection">
            <MemoryGrowthDetectionField
              name="enabled"
              LabelComponent={OverrideGroupPencilLabel}
            />
          </OverrideGroupProvider>
        }
      />
    ),
    stallDetection: (
      <StallDetectionSettingsContainer
        stallDetectionField={
          <OverrideGroupProvider name="stallDetection">
            <StallDetectionField LabelComponent={OverrideGroupPencilLabel} />
          </OverrideGroupProvider>
        }
      />
    ),
    recommendationPercentile: (
      <RecommendationPercentileSettingsContainer
        recommendationPercentileField={
          <OverrideGroupProvider name="recommendationPercentile">
            <RecommendationPercentileField
              LabelComponent={OverrideGroupPencilLabel}
            />
          </OverrideGroupProvider>
        }
      />
    ),
    workloadResourceLimits: (
      <ResourceLimitsSettingsContainer
        resourceLimitsField={
          <OverrideGroupProvider name="resourceLimits">
            <ResourceLimitsField LabelComponent={OverrideGroupPencilLabel} />
          </OverrideGroupProvider>
        }
      />
    ),
    workloadConstraints: (
      <WorkloadConstraintsSettingsContainer
        adornment={
          <ContainerSettingsChips
            workload={workload}
            fieldName="workloadConstraints"
          />
        }
        workloadConstraintsField={
          <WorkloadConstraintsSettingsArrayField workload={workload} />
        }
      />
    ),
    lookbackPeriod: (
      <LookbackPeriodSettingsContainer
        lookbackPeriodSettingField={
          <OverrideGroupProvider name="lookbackPeriod">
            <LookbackField LabelComponent={OverrideGroupPencilLabel} />
          </OverrideGroupProvider>
        }
      />
    ),
    startupMetrics: (
      <StartupMetricsSettingsContainer
        startupMetricsField={
          <OverrideGroupProvider name="startupMetrics">
            <StartupMetricsField LabelComponent={OverrideGroupPencilLabel} />
          </OverrideGroupProvider>
        }
      />
    ),
    hpaConverter: (
      <HpaConverterSettingsContainer
        hpaConverterField={
          <OverrideGroupProvider name="hpaConverter">
            <HpaConverterField policyName={workload.scalingPolicyName ?? ''} />
          </OverrideGroupProvider>
        }
      />
    ),
    predictiveScaling: (
      <PredictiveScalingSettingsContainer
        predictiveScalingField={
          <OverrideGroupProvider name="predictiveScaling">
            <RffControlsProvider namePrefix="cpu">
              <PredictiveScalingField name="enabled" />
            </RffControlsProvider>
          </OverrideGroupProvider>
        }
      />
    ),
  };

  return <>{settingContent[settingName]}</>;
};
